import React, { useRef } from 'react';
import { Board, Position, Mark, GameState } from '../types';
import { feedbackService } from '../services/FeedbackService';
import { FullScreenToggle } from './FullScreenToggle';
import './GameBoard.css';

interface GameBoardProps {
  gameState: GameState;
  onCellClick: (position: Position) => void;
  theme?: string;
  disabled?: boolean;
}

const GameBoard: React.FC<GameBoardProps> = ({
  gameState,
  onCellClick,
  theme = 'default',
  disabled = false
}) => {
  const { board, winningCells = [] } = gameState;
  const cellRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const handleCellClick = (position: Position) => {
    if (disabled) return;
    
    const cellKey = `${position.row}-${position.col}`;
    const cellElement = cellRefs.current[cellKey];
    const cell = board.cells[position.row][position.col];
    
    if (cell.mark) {
      // Cell is already occupied - show invalid move feedback
      feedbackService.showMoveInvalidFeedback(cellElement || undefined, 'Cell already occupied');
    } else {
      // Valid move - show success feedback
      feedbackService.showMoveValidFeedback(cellElement || undefined);
    }
    
    onCellClick(position);
  };

  const isCellWinning = (position: Position): boolean => {
    return winningCells.some(
      winPos => winPos.row === position.row && winPos.col === position.col
    );
  };

  const getCellClassName = (cell: any, position: Position): string => {
    const baseClass = 'game-cell';
    const themeClass = `game-cell--${theme}`;
    const markClass = cell.mark ? `game-cell--${cell.mark.toLowerCase()}` : '';
    const winningClass = isCellWinning(position) ? 'game-cell--winning' : '';
    const emptyClass = !cell.mark ? 'game-cell--empty' : '';
    const disabledClass = disabled ? 'game-cell--disabled' : '';

    return [baseClass, themeClass, markClass, winningClass, emptyClass, disabledClass]
      .filter(Boolean)
      .join(' ');
  };

  return (
    <div className={`game-board game-board--${theme}`}>
      <FullScreenToggle />
      <div className="game-board__grid">
        {board.cells.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const position: Position = { row: rowIndex, col: colIndex };
            return (
              <button
                key={`${rowIndex}-${colIndex}`}
                ref={(el) => {
                  cellRefs.current[`${rowIndex}-${colIndex}`] = el;
                }}
                className={getCellClassName(cell, position)}
                onClick={() => handleCellClick(position)}
                disabled={disabled || !!cell.mark}
                aria-label={`Cell ${rowIndex + 1}, ${colIndex + 1}${
                  cell.mark ? `, marked with ${cell.mark}` : ', empty'
                }`}
              >
                <span className="game-cell__mark">
                  {cell.mark || ''}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default GameBoard;